/**
 * Navigation Module
 * Handles main navigation interactions and mega menu positioning
 */

import { debounce, getViewport, devLog } from './utils.js';

class Navigation {
    constructor() {
        this.init();
    }

    init() {
        this.bindEvents();
        this.initMegaMenuPositioning();
        this.deduplicateActiveNavItems();
    }

    bindEvents() {
        // Add navigation event listeners
        document.addEventListener('click', this.handleDocumentClick.bind(this));
        
        // Add hover and focus events for mega menu positioning (both main and DIPG)
        const megaMenuItems = document.querySelectorAll('.main-nav .menu-item.mega-menu, .dipg-navigation .menu-item.dipg-mega-menu');
        megaMenuItems.forEach(item => {
            item.addEventListener('mouseenter', this.handleMegaMenuHover.bind(this));
            item.addEventListener('mouseleave', this.handleMegaMenuLeave.bind(this));
            item.addEventListener('focusin', this.handleMegaMenuFocus.bind(this));
            item.addEventListener('focusout', this.handleMegaMenuBlur.bind(this));
        });

        // Recalculate positions on window resize
        window.addEventListener('resize', debounce(() => {
            this.repositionVisibleMegaMenus();
        }, 250));
    }

    handleDocumentClick(event) {
        // Handle clicks outside navigation to close dropdowns
        const nav = document.querySelector('.main-nav');
        if (nav && !nav.contains(event.target)) {
            this.closeAllDropdowns();
        }
    }

    handleMegaMenuHover(event) {
        const menuItem = event.currentTarget;
        const subMenu = menuItem.querySelector('.sub-menu.mega-menu');
        
        if (subMenu) {
            // Position the mega menu before showing it
            this.positionMegaMenu(menuItem, subMenu);
        }
    }

    handleMegaMenuLeave(event) {
        // Optional: Add any cleanup when leaving mega menu
        // Usually handled by CSS :hover already
    }

    handleMegaMenuFocus(event) {
        const menuItem = event.currentTarget;
        const subMenu = menuItem.querySelector('.sub-menu.mega-menu');
        
        if (subMenu) {
            // Position the mega menu before showing it
            this.positionMegaMenu(menuItem, subMenu);
        }
    }

    handleMegaMenuBlur(event) {
        // Optional: Add any cleanup when losing focus
        // Usually handled by CSS :focus-within already
    }

    closeAllDropdowns() {
        const dropdowns = document.querySelectorAll('.main-nav .sub-menu, .dipg-navigation .sub-menu');
        dropdowns.forEach(dropdown => {
            dropdown.style.display = 'none';
        });
    }

    positionMegaMenu(menuItem, megaMenu) {
        // Check if this is a DIPG mega menu - if so, center it and skip complex positioning
        const isDIPG = menuItem.closest('.dipg-navigation') !== null;
        
        if (isDIPG) {
            // For DIPG mega menus, let CSS handle centering and just set up the hover bridge
            const parentAnchor = menuItem.querySelector('a');
            
            // Reset any existing positioning to let CSS handle it
            megaMenu.style.left = '';
            megaMenu.style.right = '';
            megaMenu.style.width = '';
            
            // Create hover bridge by sizing the ::after pseudo-element to match parent anchor
            if (parentAnchor) {
                const anchorRect = parentAnchor.getBoundingClientRect();
                const bridgeWidth = anchorRect.width;
                
                // Set the bridge width via CSS custom property
                megaMenu.style.setProperty('--bridge-width', `${bridgeWidth}px`);
            }
            
            devLog('DIPG mega menu positioned by CSS with hover bridge:', {
                bridgeWidth: parentAnchor ? parentAnchor.getBoundingClientRect().width : 'unknown'
            });
            return;
        }

        // Get viewport and container dimensions
        const viewport = getViewport();

        // Get the navigation container (main navigation only)
        const navContainer = document.querySelector('.main-navigation .inside-navigation') || 
                          document.querySelector('.main-navigation') ||
                          document.querySelector('.main-nav').parentElement;

        const navRect = navContainer.getBoundingClientRect();
        const menuItemRect = menuItem.getBoundingClientRect();

        // Reset positioning to get natural dimensions
        megaMenu.style.left = '';
        megaMenu.style.right = '';
        megaMenu.style.width = '';

        // Get mega menu dimensions
        megaMenu.style.visibility = 'hidden';
        megaMenu.style.display = 'block';
        const megaMenuRect = megaMenu.getBoundingClientRect();
        megaMenu.style.visibility = '';
        megaMenu.style.display = '';

        // Default: Left edge of mega menu aligns with left edge of parent menu item
        const defaultLeft = menuItemRect.left;
        
        // Check if this would overflow the right edge of viewport
        const wouldOverflowRight = (defaultLeft + megaMenuRect.width) > (viewport.width - 30);
        
        let finalLeft;
        if (wouldOverflowRight) {
            // Shift left to maintain 30px buffer from right edge
            finalLeft = viewport.width - megaMenuRect.width - 30;
            devLog('Mega menu would overflow, shifting left to maintain 30px buffer');
        } else {
            // Use default positioning (left edge alignment)
            finalLeft = defaultLeft;
            devLog('Mega menu using default left edge alignment');
        }
        
        // Ensure we don't go beyond left edge of viewport
        finalLeft = Math.max(0, finalLeft);

        // Set position relative to the navigation container
        const relativeLeft = finalLeft - navRect.left;
        megaMenu.style.left = `${relativeLeft}px`;

        // Ensure mega menu doesn't go off-screen on mobile (below 1024px)
        if (viewport.width < 1024) {
            megaMenu.style.left = '0';
            megaMenu.style.right = '0';
            megaMenu.style.width = '100%';
        }

        devLog('Main navigation mega menu positioned:', {
            viewportWidth: viewport.width,
            megaMenuWidth: megaMenuRect.width,
            finalLeft: finalLeft,
            relativeLeft: relativeLeft,
            isMobile: viewport.width < 1024
        });
    }

    repositionVisibleMegaMenus() {
        // Reposition any currently visible mega menus (both main and DIPG)
        const visibleMegaMenus = document.querySelectorAll('.main-nav .menu-item.mega-menu:hover, .main-nav .menu-item.mega-menu:focus-within, .dipg-navigation .menu-item.dipg-mega-menu:hover, .dipg-navigation .menu-item.dipg-mega-menu:focus-within');
        visibleMegaMenus.forEach(menuItem => {
            const subMenu = menuItem.querySelector('.sub-menu.mega-menu');
            if (subMenu) {
                this.positionMegaMenu(menuItem, subMenu);
            }
        });
    }

    initMegaMenuPositioning() {
        // Add class to identify mega menu items for styling (both main and DIPG)
        const megaMenuItems = document.querySelectorAll('.main-nav .menu-item .mega-menu-grid, .main-nav .menu-item .sub-menu .mega-menu-grid, .dipg-navigation .menu-item .mega-menu-grid, .dipg-navigation .menu-item .sub-menu .mega-menu-grid');
        megaMenuItems.forEach(grid => {
            const menuItem = grid.closest('.menu-item');
            const subMenu = grid.closest('.sub-menu');
            
            if (menuItem && subMenu) {
                // Determine if this is a DIPG navigation
                const isDIPG = menuItem.closest('.dipg-navigation') !== null;
                
                if (isDIPG) {
                    menuItem.classList.add('dipg-mega-menu');
                } else {
                    menuItem.classList.add('mega-menu');
                }
                subMenu.classList.add('mega-menu');
                
                // Re-bind hover and focus events for dynamically detected mega menus
                menuItem.addEventListener('mouseenter', this.handleMegaMenuHover.bind(this));
                menuItem.addEventListener('mouseleave', this.handleMegaMenuLeave.bind(this));
                menuItem.addEventListener('focusin', this.handleMegaMenuFocus.bind(this));
                menuItem.addEventListener('focusout', this.handleMegaMenuBlur.bind(this));
            }
        });

        // Also check for items that already have mega-menu class in WordPress
        const existingMegaMenus = document.querySelectorAll('.main-nav .menu-item.mega-menu, #main-navigation .menu-item.mega-menu, .dipg-navigation .menu-item.dipg-mega-menu');
        existingMegaMenus.forEach(menuItem => {
            const subMenu = menuItem.querySelector('.sub-menu');
            if (subMenu && !subMenu.classList.contains('mega-menu')) {
                subMenu.classList.add('mega-menu');
            }
            
            // Ensure hover and focus events are bound
            menuItem.addEventListener('mouseenter', this.handleMegaMenuHover.bind(this));
            menuItem.addEventListener('mouseleave', this.handleMegaMenuLeave.bind(this));
            menuItem.addEventListener('focusin', this.handleMegaMenuFocus.bind(this));
            menuItem.addEventListener('focusout', this.handleMegaMenuBlur.bind(this));
        });

        // Mega menu positioning initialized
    }

    // Keep only the most specific active class on top-level nav items.
    // Priority: current-menu-item > current-menu-parent > current-menu-ancestor
    deduplicateActiveNavItems() {
        const navLists = document.querySelectorAll(
            '#main-navigation > ul.nav, .main-nav > ul, ul.secondary-nav'
        );
        if (!navLists.length) return;

        navLists.forEach(nav => {
            const items = Array.from(nav.querySelectorAll(':scope > li.menu-item'));

            const hasExact  = items.some(i => i.classList.contains('current-menu-item') || i.classList.contains('current_page_item'));
            const hasParent = items.some(i => i.classList.contains('current-menu-parent') || i.classList.contains('current_page_parent'));

            items.forEach(item => {
                const isExact  = item.classList.contains('current-menu-item') || item.classList.contains('current_page_item');
                const isParent = item.classList.contains('current-menu-parent') || item.classList.contains('current_page_parent');

                const shouldStrip = (hasExact && !isExact) || (!hasExact && hasParent && !isParent);
                if (!shouldStrip) return;

                // Disable transition to prevent blink on page load, restore after paint
                const link = item.querySelector(':scope > a');
                if (link) link.style.transition = 'none';

                const toRemove = hasExact
                    ? ['current-menu-ancestor', 'current-menu-parent', 'current_page_ancestor', 'current_page_parent']
                    : ['current-menu-ancestor', 'current_page_ancestor'];
                toRemove.forEach(cls => item.classList.remove(cls));

                if (link) requestAnimationFrame(() => requestAnimationFrame(() => { link.style.transition = ''; }));
            });
        });
    }

    // Utility function for debouncing resize events

}

export default Navigation; 